import { Link } from 'react-router-dom'
import { m } from 'motion/react'
import { Button } from '@/components/ui'
import { FugueContactForm } from '@/components/FugueContactForm'
import { useSEO } from '@/hooks/useSEO'
import { useScrollReveal, useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { ArrowRight } from 'lucide-react'
import { competitors } from '@/data/compare'

export default function CompareIndexPage() {
  const breadcrumbs = generateBreadcrumbs('/compare', breadcrumbLabels)

  useSEO({
    title: 'Compare Fugue Lab',
    description:
      'How Fugue Lab compares to Acceptd, Squarespace, Wix, and WordPress for music festivals, ensembles, schools, and churches.',
    url: '/compare',
    canonical: '/compare',
    breadcrumbs,
  })

  const heroRef = useHeroReveal<HTMLElement>(0.2)
  const gridRef = useStaggeredGrid<HTMLElement>('.compare-card', { stagger: 0.1 })
  const ctaRef = useScrollReveal<HTMLElement>('.cta-content')

  return (
    <div className="mx-auto max-w-6xl space-y-20 px-4 py-16">
      <section ref={heroRef} className="mx-auto max-w-3xl space-y-6 text-center">
        <h1 className="text-fluid-h1">How Fugue Lab compares</h1>
        <p className="text-fluid-h4 text-muted-foreground">
          You are probably weighing us against a platform you already use. Good. Here is the honest comparison for each
          one, including where they genuinely fit.
        </p>
      </section>

      <section ref={gridRef} className="grid gap-6 md:grid-cols-2">
        {competitors.map((competitor) => (
          <Link
            key={competitor.slug}
            to={`/compare/${competitor.slug}`}
            className="compare-card border-border/50 from-primary/5 hover:border-primary/30 group rounded-2xl border bg-linear-to-b to-transparent p-8 transition-all duration-300 hover:shadow-lg"
          >
            <h2 className="text-xl font-bold">Fugue Lab vs {competitor.name}</h2>
            <p className="text-muted-foreground mt-3 text-sm leading-relaxed">{competitor.seoDescription}</p>
            <span className="text-primary mt-4 inline-flex items-center gap-2 text-sm font-medium">
              Read the comparison <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </span>
          </Link>
        ))}
      </section>

      <section
        ref={ctaRef}
        className="cta-content from-primary/10 via-primary/5 border-primary/20 space-y-6 rounded-2xl border bg-linear-to-br to-transparent p-8 text-center md:p-12"
      >
        <h2 className="text-fluid-h3">Using something not listed here?</h2>
        <p className="text-muted-foreground mx-auto max-w-2xl">
          Bring whatever you run today to a call. You get a straight answer on whether switching is worth it, including
          when the answer is no.
        </p>
        <a href="/book" target="_blank" rel="noopener noreferrer">
          <m.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            className="inline-block"
          >
            <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
              Book a Free Call <ArrowRight className="ml-1 h-5 w-5" />
            </Button>
          </m.div>
        </a>
      </section>

      <FugueContactForm />
    </div>
  )
}
